import { useState, useEffect } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { X, Send, Mail, Users, AlertCircle, Eye, Code } from 'lucide-react'
import { brevoApi, BrevoList, SegmentMapping } from '@/lib/api'
import { useToast } from '@/components/ui/Toast'
import { cn } from '@/lib/utils'

interface CampaignModalProps {
  isOpen: boolean
  onClose: () => void
  lists: BrevoList[]
  mappings: SegmentMapping[]
  preselectedSegment?: string | null
}

export default function CampaignModal({
  isOpen,
  onClose,
  lists,
  mappings,
  preselectedSegment
}: CampaignModalProps) {
  const toast = useToast()
  const queryClient = useQueryClient()

  const [name, setName] = useState('')
  const [subject, setSubject] = useState('')
  const [htmlContent, setHtmlContent] = useState(getDefaultTemplate())
  const [selectedListIds, setSelectedListIds] = useState<number[]>([])
  const [selectedSegment, setSelectedSegment] = useState<string | null>(preselectedSegment || null)
  const [showPreview, setShowPreview] = useState(false)

  // When preselected segment changes, update selected list
  useEffect(() => {
    if (preselectedSegment) {
      const mapping = mappings.find(m => m.segment_name === preselectedSegment)
      if (mapping?.brevo_list_id) {
        setSelectedListIds([mapping.brevo_list_id])
        setSelectedSegment(preselectedSegment)
      }
    }
  }, [preselectedSegment, mappings])

  const sendMutation = useMutation({
    mutationFn: (data: {
      name: string
      subject: string
      html_content: string
      segment_name?: string
      list_ids: number[]
    }) => brevoApi.sendCampaign(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['brevo-history'] })
      toast.success('Campana enviada correctamente')
      onClose()
    },
    onError: (error: any) => {
      toast.error('Error enviando campana', error.response?.data?.detail || error.message)
    }
  })

  const handleSegmentSelect = (segmentName: string) => {
    const mapping = mappings.find(m => m.segment_name === segmentName)
    if (mapping?.brevo_list_id) {
      if (selectedSegment === segmentName) {
        // Deselect
        setSelectedSegment(null)
        setSelectedListIds(prev => prev.filter(id => id !== mapping.brevo_list_id))
      } else {
        // Select
        setSelectedSegment(segmentName)
        if (!selectedListIds.includes(mapping.brevo_list_id)) {
          setSelectedListIds(prev => [...prev, mapping.brevo_list_id!])
        }
      }
    }
  }

  const handleListToggle = (listId: number) => {
    setSelectedListIds(prev =>
      prev.includes(listId)
        ? prev.filter(id => id !== listId)
        : [...prev, listId]
    )
    // Clear segment selection if manually selecting lists
    setSelectedSegment(null)
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()

    if (!name.trim()) {
      toast.error('Error', 'El nombre de la campana es requerido')
      return
    }
    if (!subject.trim()) {
      toast.error('Error', 'El asunto del email es requerido')
      return
    }
    if (!htmlContent.trim()) {
      toast.error('Error', 'El contenido del email es requerido')
      return
    }
    if (selectedListIds.length === 0) {
      toast.error('Error', 'Debes seleccionar al menos una lista de destinatarios')
      return
    }

    sendMutation.mutate({
      name,
      subject,
      html_content: htmlContent,
      segment_name: selectedSegment || undefined,
      list_ids: selectedListIds
    })
  }

  const totalRecipients = lists
    .filter(l => selectedListIds.includes(l.id))
    .reduce((sum, l) => sum + l.total_subscribers, 0)

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        {/* Backdrop */}
        <div
          className="fixed inset-0 bg-slate-500/75 dark:bg-slate-900/75 transition-opacity"
          onClick={onClose}
        />

        {/* Modal */}
        <div className="inline-block w-full max-w-4xl my-8 overflow-hidden text-left align-middle transition-all transform bg-white dark:bg-slate-800 rounded-lg shadow-xl">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 dark:border-slate-700">
            <div className="flex items-center gap-3">
              <Mail className="w-6 h-6 text-primary-600" />
              <h2 className="text-lg font-semibold text-slate-900 dark:text-white">
                Nueva Campana de Email
              </h2>
            </div>
            <button
              onClick={onClose}
              className="p-2 text-slate-400 hover:text-slate-500 dark:hover:text-slate-300"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Content */}
          <form onSubmit={handleSubmit}>
            <div className="px-6 py-4 max-h-[70vh] overflow-y-auto space-y-6">
              {/* Campaign Info */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                    Nombre de la campana (interno)
                  </label>
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Ej: Promocion Navidad 2024"
                    className="input w-full"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                    Asunto del email
                  </label>
                  <input
                    type="text"
                    value={subject}
                    onChange={(e) => setSubject(e.target.value)}
                    placeholder="Ej: Ofertas exclusivas para ti"
                    className="input w-full"
                    required
                  />
                </div>
              </div>

              {/* Segment Selection */}
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                  Seleccionar Segmento RFM (opcional)
                </label>
                <div className="flex flex-wrap gap-2">
                  {mappings.filter(m => m.brevo_list_id).map((mapping) => (
                    <button
                      key={mapping.segment_name}
                      type="button"
                      onClick={() => handleSegmentSelect(mapping.segment_name)}
                      className={cn(
                        "px-3 py-2 rounded-lg text-sm font-medium transition-colors border",
                        selectedSegment === mapping.segment_name
                          ? "bg-primary-100 border-primary-500 text-primary-700 dark:bg-primary-900/30 dark:border-primary-400 dark:text-primary-300"
                          : "bg-slate-100 border-gray-200 text-gray-700 hover:bg-gray-200 dark:bg-slate-700 dark:border-gray-600 dark:text-slate-300"
                      )}
                    >
                      <span className="flex items-center gap-2">
                        {mapping.segment_name}
                        <span className="text-xs opacity-70">
                          ({mapping.last_sync_count})
                        </span>
                      </span>
                    </button>
                  ))}
                </div>
              </div>

              {/* List Selection */}
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                  O seleccionar Listas de Brevo
                </label>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                  {lists.map((list) => (
                    <label
                      key={list.id}
                      className={cn(
                        "flex items-center p-3 rounded-lg border cursor-pointer transition-colors",
                        selectedListIds.includes(list.id)
                          ? "bg-primary-50 border-primary-500 dark:bg-primary-900/20 dark:border-primary-400"
                          : "bg-white border-gray-200 hover:bg-slate-50 dark:bg-slate-800 dark:border-gray-700 dark:hover:bg-gray-700"
                      )}
                    >
                      <input
                        type="checkbox"
                        checked={selectedListIds.includes(list.id)}
                        onChange={() => handleListToggle(list.id)}
                        className="mr-3"
                      />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-slate-900 dark:text-white truncate">
                          {list.name}
                        </p>
                        <p className="text-xs text-slate-500 dark:text-slate-400">
                          {list.total_subscribers} contactos
                        </p>
                      </div>
                    </label>
                  ))}
                </div>
              </div>

              {/* Total Recipients */}
              {selectedListIds.length > 0 && (
                <div className="flex items-center gap-2 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                  <Users className="w-5 h-5 text-primary-600 dark:text-blue-400" />
                  <span className="text-sm text-blue-800 dark:text-blue-200">
                    Total de destinatarios: <strong>{totalRecipients.toLocaleString()}</strong> contactos
                  </span>
                </div>
              )}

              {/* HTML Content */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300">
                    Contenido HTML
                  </label>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => setShowPreview(!showPreview)}
                      className={cn(
                        "btn btn-xs",
                        showPreview ? "btn-primary" : "btn-secondary"
                      )}
                    >
                      {showPreview ? (
                        <>
                          <Code className="w-3 h-3 mr-1" />
                          Codigo
                        </>
                      ) : (
                        <>
                          <Eye className="w-3 h-3 mr-1" />
                          Preview
                        </>
                      )}
                    </button>
                  </div>
                </div>

                {showPreview ? (
                  <div
                    className="w-full h-96 border border-gray-300 dark:border-gray-600 rounded-lg p-4 bg-white overflow-auto"
                    dangerouslySetInnerHTML={{ __html: htmlContent }}
                  />
                ) : (
                  <textarea
                    value={htmlContent}
                    onChange={(e) => setHtmlContent(e.target.value)}
                    className="input w-full h-96 font-mono text-sm"
                    placeholder="<html>...</html>"
                    required
                  />
                )}
              </div>

              {/* Warning */}
              <div className="flex items-start gap-3 p-4 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-200 dark:border-amber-800">
                <AlertCircle className="w-5 h-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-amber-800 dark:text-amber-200">
                  <p className="font-medium mb-1">Antes de enviar:</p>
                  <ul className="list-disc list-inside space-y-1 opacity-90">
                    <li>Revisa el contenido y el preview del email</li>
                    <li>Verifica que los destinatarios son correctos</li>
                    <li>La campana se enviara inmediatamente al confirmar</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50">
              <button
                type="button"
                onClick={onClose}
                className="btn btn-secondary"
                disabled={sendMutation.isPending}
              >
                Cancelar
              </button>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={sendMutation.isPending || selectedListIds.length === 0}
              >
                {sendMutation.isPending ? (
                  <>
                    <span className="animate-spin mr-2">...</span>
                    Enviando...
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4 mr-2" />
                    Enviar Campana ({totalRecipients.toLocaleString()} destinatarios)
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}

function getDefaultTemplate(): string {
  return `<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>Email</title>
</head>
<body style="margin: 0; padding: 0; font-family: Arial, sans-serif; background-color: #f4f4f4;">
  <table role="presentation" style="width: 100%; border-collapse: collapse;">
    <tr>
      <td style="padding: 40px 0;">
        <table role="presentation" style="max-width: 600px; margin: 0 auto; background-color: #ffffff; border-radius: 8px; overflow: hidden; box-shadow: 0 2px 4px rgba(0,0,0,0.1);">
          <!-- Header -->
          <tr>
            <td style="background-color: #2563eb; padding: 30px; text-align: center;">
              <h1 style="color: #ffffff; margin: 0; font-size: 24px;">JVS Informatica</h1>
            </td>
          </tr>

          <!-- Content -->
          <tr>
            <td style="padding: 40px 30px;">
              <h2 style="color: #333333; margin-top: 0;">Hola,</h2>
              <p style="color: #666666; line-height: 1.6;">
                Escribe aqui el contenido de tu email...
              </p>
              <p style="color: #666666; line-height: 1.6;">
                Lorem ipsum dolor sit amet, consectetur adipiscing elit.
                Sed do eiusmod tempor incididunt ut labore et dolore magna aliqua.
              </p>

              <!-- CTA Button -->
              <table role="presentation" style="margin: 30px 0;">
                <tr>
                  <td style="background-color: #2563eb; border-radius: 6px;">
                    <a href="https://www.jvs-informatica.com" style="display: inline-block; padding: 14px 30px; color: #ffffff; text-decoration: none; font-weight: bold;">
                      Ver Ofertas
                    </a>
                  </td>
                </tr>
              </table>

              <p style="color: #666666; line-height: 1.6;">
                Saludos,<br>
                El equipo de JVS Informatica
              </p>
            </td>
          </tr>

          <!-- Footer -->
          <tr>
            <td style="background-color: #f8f9fa; padding: 20px 30px; text-align: center; border-top: 1px solid #e9ecef;">
              <p style="color: #999999; font-size: 12px; margin: 0;">
                JVS Informatica | www.jvs-informatica.com
              </p>
              <p style="color: #999999; font-size: 12px; margin: 10px 0 0 0;">
                Si no deseas recibir mas emails, puedes <a href="{{ unsubscribe }}" style="color: #2563eb;">darte de baja</a>
              </p>
            </td>
          </tr>
        </table>
      </td>
    </tr>
  </table>
</body>
</html>`
}
